import {FC} from 'react'
import {UseFormReturn} from 'react-hook-form'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {useTranslation} from '@/translations/i18n'
import {MailerFormValidationSchema} from '@/features/mailer/types'
import {PatientFiltersPanel} from '@/features/mailer/components/patient-filters-panel/PatientFiltersPanel'
import {ConsultantFiltersPanel} from '@/features/mailer/components/consultant-filters-panel/ConsultantFiltersPanel'
import {MailerActiveFiltersBar} from '@/features/mailer/components/mailer-active-filters-bar/MailerActiveFiltersBar'
import {Label} from '@/components/ui/label/Label'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {WarningLabel} from '@/components/ui/warning-label/WarningLabel'
import {StyledMailerSection} from '@/pages/mailer/style'

interface MailerRecipientSectionProps {
    form: UseFormReturn<MailerFormValidationSchema>
    hasPatientFilters: boolean
    hasConsultantFilters: boolean
    showAllRecipientsBanner: boolean
}

export const MailerRecipientSection: FC<MailerRecipientSectionProps> = ({
    form,
    hasPatientFilters,
    hasConsultantFilters,
    showAllRecipientsBanner
}) => {
    const {t} = useTranslation()
    const {
        register,
        watch,
        formState: {errors}
    } = form

    const includePatients = watch('includePatients')
    const includeConsultants = watch('includeConsultants')

    const recipientError = errors.includePatients?.message

    return (
        <StyledMailerSection>
            <Label>{t('mailer:recipients_section_title')}</Label>

            <Flexbox gap={4} align="center">
                <Checkbox id="includePatients" label={t('mailer:include_patients')} {...register('includePatients')} />
                <Checkbox
                    id="includeConsultants"
                    label={t('mailer:include_consultants')}
                    {...register('includeConsultants')}
                />
            </Flexbox>

            {recipientError && <InputHelpText error={t('mailer:select_at_least_one_recipient_type')} />}

            {showAllRecipientsBanner && <WarningLabel text={t('mailer:all_recipients_no_filters_banner')} />}

            <MailerActiveFiltersBar form={form} />

            {includePatients && <PatientFiltersPanel form={form} hasFilters={hasPatientFilters} />}
            {includeConsultants && <ConsultantFiltersPanel form={form} hasFilters={hasConsultantFilters} />}
        </StyledMailerSection>
    )
}
